"use client";

import { useEffect, useState } from "react";
import type { Vehicle } from "@/lib/types";
import VehicleCard from "./VehicleCard";
import type { SpecFilters } from "./SearchFilterBlock";

export interface DateRange {
  from: string;
  to: string;
}

export interface VehicleCarryParams {
  pickup_time: string;
  return_time: string;
  pickup_location_id: number | null;
  return_location_id: number | null;
}

interface VehicleGridProps {
  vehicles: Vehicle[];
  blurMap?: Record<string, string | undefined>;
  versionMap?: Record<string, number>;
  dateRange: DateRange | null;
  specFilters: SpecFilters;
  carry: VehicleCarryParams;
  assetBaseUrl: string;
}

export default function VehicleGrid({
  vehicles,
  blurMap,
  versionMap,
  dateRange,
  specFilters,
  carry,
  assetBaseUrl,
}: VehicleGridProps) {
  const [availableIds, setAvailableIds] = useState<Set<number> | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!dateRange) {
      setAvailableIds(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch("/api/vehicles/availability/batch", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            vehicle_ids: vehicles.map((v) => v.id),
            date_from: dateRange.from,
            date_to: dateRange.to,
          }),
        });
        // Map of vehicleId -> hasConflict (true = unavailable for the range).
        const conflicts: Record<number, boolean> = await res.json();
        if (cancelled) return;
        setAvailableIds(
          new Set(vehicles.filter((v) => !conflicts[v.id]).map((v) => v.id)),
        );
      } catch {
        if (!cancelled) setAvailableIds(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dateRange, vehicles]);

  const filtered = vehicles.filter((v) => {
    if (availableIds && !availableIds.has(v.id)) return false;
    if (specFilters.transmission && v.transmission !== specFilters.transmission) return false;
    if (specFilters.body_type && v.body_type !== specFilters.body_type) return false;
    return true;
  });

  return (
    <section id="fleet" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Automobiliai</h2>

      {loading && (
        <div className="text-center py-12 text-gray-500">
          <div className="inline-block w-6 h-6 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin mb-3" />
          <p className="text-sm">Tikrinama galimybė...</p>
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">
            {dateRange
              ? "Pasirinktomis datomis laisvų automobilių nėra. Pabandykite kitas datas arba atlaisvinkite filtrus."
              : (specFilters.transmission || specFilters.body_type)
                ? "Pagal pasirinktus filtrus automobilių nėra."
                : "Šiuo metu automobilių nėra."}
          </p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((vehicle) => (
            <VehicleCard
              key={vehicle.id}
              vehicle={vehicle}
              dateRange={dateRange}
              carry={carry}
              blurDataURL={vehicle.image ? blurMap?.[vehicle.image] : undefined}
              version={vehicle.image ? versionMap?.[vehicle.image] : undefined}
              assetBaseUrl={assetBaseUrl}
            />
          ))}
        </div>
      )}
    </section>
  );
}
